import { useSyncExternalStore } from 'react';

// The portal's only cookies, the website's (sculpt-agent.ai, src/analytics.ts): each is loaded once the visitor accepts
// its purpose in the cookie banner. Google Analytics (GA4) measures the audience and the sign-ups, LinkedIn's Insight Tag
// and, through consent mode, Google's tag measure our ads. One consent per purpose (CNIL). The choice is kept in this
// origin's storage, under the website's key: account.sculpt-agent.ai cannot read the website's, so the banner asks here
// too. The tags are set up from here, not from inline scripts, so the CSP keeps script-src to 'self' and their hosts
// (security-headers.conf). A tag without its id (dev, tests) is left out of the banner, and without either there is no
// banner.
export const GA_MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || '';
export const LINKEDIN_PARTNER_ID = (import.meta.env.VITE_LINKEDIN_PARTNER_ID as string | undefined) || '';

export type Purpose = 'measurement' | 'advertising';
export type Choices = Partial<Record<Purpose, boolean>>;
const STORAGE_KEY = 'pfa-analytics';
// The choice is asked again after 6 months, and the GA cookies expire after 13 months (CNIL guidance).
const CHOICE_MAX_AGE_MS = 182 * 24 * 3600 * 1000;
const COOKIE_EXPIRES_S = 13 * 30 * 24 * 3600;
// The recipients each purpose was asked for, as a version stored with the choice (`v`; none is 1), as on the website. A
// purpose accepted for an earlier version is asked again, sooner than 6 months (privacy policy, clause 9); a refusal
// stands. The portal's banner named LinkedIn and Google for advertising from the start: both purposes are at 1.
const RECIPIENTS: Record<Purpose, number> = { measurement: 1, advertising: 1 };

function readChoices(): Choices {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as (Choices & { at: number; v?: Partial<Record<Purpose, number>> }) | null;
    if (!stored || Date.now() - stored.at >= CHOICE_MAX_AGE_MS) return {};
    const kept = (p: Purpose) => (stored[p] === true && (stored.v?.[p] ?? 1) < RECIPIENTS[p] ? undefined : stored[p]);
    return { measurement: kept('measurement'), advertising: kept('advertising') };
  } catch {}
  return {};
}

let choices = readChoices();
// "Manage cookies", at the bottom of every page, shows the banner again, whatever was chosen.
let reopened = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

declare global {
  interface Window {
    dataLayer: unknown[];
    _linkedin_partner_id: string;
    _linkedin_data_partner_ids: string[];
    gtag?: (...args: unknown[]) => void;
    lintrk?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

// Google's consent mode v2, from the visitor's two choices.
function googleConsent(c: Choices) {
  const ads = c.advertising === true ? 'granted' : 'denied';
  return {
    analytics_storage: c.measurement === true ? 'granted' : 'denied',
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
}

function loadGoogleAnalytics() {
  window.dataLayer = window.dataLayer || [];
  // gtag pushes its `arguments` object, not an array: gtag.js reads only that shape.
  function gtag(..._args: unknown[]) {
    window.dataLayer.push(arguments);
  }
  // Shared with the pages that send an event (sign-up), once the visitor accepts the measurement.
  window.gtag = gtag;
  // Google's consent mode: the tag loads once the measurement is accepted, and its advertising signals (Google Ads
  // conversions, remarketing) follow the advertising choice, updated when the visitor changes it.
  gtag('consent', 'default', googleConsent(choices));
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { cookie_expires: COOKIE_EXPIRES_S });
  addScript(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`);
}

// LinkedIn's snippet, without its <noscript> pixel: without JavaScript there is no banner, so no consent.
function loadLinkedInInsightTag() {
  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  // The snippet's queue: a conversion tracked before insight.min.js has loaded is sent once it has.
  if (!window.lintrk) {
    const lintrk: ((...args: unknown[]) => void) & { q: unknown[][] } = Object.assign(
      (...args: unknown[]) => {
        lintrk.q.push(args);
      },
      { q: [] as unknown[][] },
    );
    window.lintrk = lintrk;
  }
  addScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
}

// A URL that carries a secret: an invitation's token in its path (/invite/<token>), a reset or verification token in
// its query (?token=), the sign-in tokens or the portal link's code in the fragment of /auth/*. Both tags send the
// page's URL (GA4 its path and query, the Insight Tag all of it), so they never run on one: their scripts load once the
// page is on a URL without a secret (CallbackPage and LinkPage drop their fragment at once), and until then what the
// pages send (sign_up, the LinkedIn conversion) waits in the tags' queues, sent once the scripts have loaded.
export function carriesSecret(url: string | URL): boolean {
  const u = new URL(url, location.href);
  return u.pathname.startsWith('/invite/') || u.searchParams.has('token') || (u.pathname.startsWith('/auth/') && u.hash.length > 1);
}

const pendingScripts: string[] = [];
let running = false;

function addScript(src: string) {
  pendingScripts.push(src);
  flushScripts();
}

function flushScripts() {
  if (pendingScripts.length === 0 || carriesSecret(location.href)) return;
  for (const src of pendingScripts.splice(0)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }
  running = true;
}

// Every navigation of the router goes through pushState / replaceState: one that leaves a URL with a secret loads the
// waiting scripts, and one to a URL with a secret (/login back to /invite/<token>), once they run, loads that page anew
// instead, where they wait again. Patched before the tags load, so that their own history listeners never see such a
// URL. Back and forward (popstate) cannot be stopped: they reload the page, after the tags may have seen it; the URLs
// left behind hold spent secrets (a used reset or verification token, an accepted invitation, an emptied fragment).
function guardHistory() {
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method].bind(history);
    history[method] = (data: unknown, unused: string, url?: string | URL | null) => {
      if (running && url != null && carriesSecret(url)) {
        if (method === 'pushState') location.assign(String(url));
        else location.replace(String(url));
        return;
      }
      original(data, unused, url);
      flushScripts();
    };
  }
  window.addEventListener('popstate', () => {
    if (running && carriesSecret(location.href)) location.reload();
    else flushScripts();
  });
}

// Cookies set by a tag on the registrable domain (.sculpt-agent.ai) or on the host.
function deleteCookies(prefix: string) {
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0].trim();
    if (!name.startsWith(prefix)) continue;
    for (const domain of ['', `; domain=${location.hostname}`, `; domain=.${location.hostname.split('.').slice(-2).join('.')}`]) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }
  }
}

const TAGS: Record<Purpose, { id: string; load: () => void; forget: () => void }> = {
  // `_ga` and `_ga_<id>`.
  measurement: { id: GA_MEASUREMENT_ID, load: loadGoogleAnalytics, forget: () => deleteCookies('_ga') },
  // `li_fat_id` and `li_giant` (the ids of a LinkedIn ad's click) and, outside the EU, `li_adsId`, also kept in local
  // storage; `_gcl_…`, which Google's tag sets under this consent (consent mode's ad_storage), also kept in local
  // storage. LinkedIn's and Google's own cookies, on their domains, are out of the portal's reach.
  advertising: {
    id: LINKEDIN_PARTNER_ID,
    load: loadLinkedInInsightTag,
    forget: () => {
      deleteCookies('li_');
      deleteCookies('_gcl_');
      try {
        localStorage.removeItem('li_adsId');
        for (const key of Object.keys(localStorage)) if (key.startsWith('_gcl_')) localStorage.removeItem(key);
      } catch {}
    },
  },
};

// The purposes the banner asks about, in its order: those whose tag has an id.
export const COOKIE_PURPOSES = (Object.keys(TAGS) as Purpose[]).filter((p) => TAGS[p].id);

const loaded = new Set<Purpose>();
function load(purpose: Purpose) {
  if (loaded.has(purpose) || !TAGS[purpose].id) return;
  loaded.add(purpose);
  TAGS[purpose].load();
}

export function getCookieChoices(): Choices {
  return choices;
}

// Whether the visitor accepted a purpose: an event is sent only then (GA4 for measurement, LinkedIn for advertising).
export const measurementGranted = (): boolean => choices.measurement === true;
export const advertisingGranted = (): boolean => choices.advertising === true;

export function setCookieChoices(next: Choices) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...next, v: RECIPIENTS, at: Date.now() }));
  } catch {}
  const withdrawn = COOKIE_PURPOSES.filter((p) => choices[p] && !next[p]);
  choices = next;
  if (loaded.has('measurement')) window.gtag?.('consent', 'update', googleConsent(next));
  reopened = false;
  COOKIE_PURPOSES.filter((p) => next[p]).forEach(load);
  // Every refused purpose, not only a withdrawn one: an acceptance asked again (RECIPIENTS) and now refused left its
  // cookies from the earlier version.
  COOKIE_PURPOSES.filter((p) => !next[p]).forEach((p) => TAGS[p].forget());
  // A tag, once loaded, keeps running until the page is left.
  if (withdrawn.some((p) => loaded.has(p))) location.reload();
  emit();
}

export function reopenCookieBanner() {
  reopened = true;
  emit();
}

if (COOKIE_PURPOSES.length > 0) guardHistory();
COOKIE_PURPOSES.filter((p) => choices[p]).forEach(load);

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

// Whether the banner shows: a purpose not chosen yet (or a stale choice), or reopened from the page's link.
export function useCookieBannerOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => COOKIE_PURPOSES.some((p) => choices[p] === undefined) || (COOKIE_PURPOSES.length > 0 && reopened),
    () => false,
  );
}
